import React from 'react'
import { Box } from '@mui/material'
import LoadingState, { LoadingVariant } from './LoadingState'
import ErrorState from './ErrorState'
import EmptyState, { EmptyStateType } from './EmptyState'

// ============================================================================
// Types
// ============================================================================

export interface DataFetchWrapperProps<T = any> {
  /** Loading state */
  loading: boolean
  /** Error state */
  error?: string | null
  /** Data array or object */
  data?: T[] | T | null
  /** Minimum items to consider as "not empty" for arrays */
  minItems?: number
  
  // Loading configuration
  /** Loading variant */
  loadingVariant?: LoadingVariant
  /** Loading message */
  loadingMessage?: string
  /** Number of skeleton rows */
  loadingRows?: number
  
  // Error configuration
  /** Error title override */
  errorTitle?: string
  /** Error retry handler */
  onRetry?: () => void
  /** Show error details */
  showErrorDetails?: boolean
  
  // Empty state configuration
  /** Empty state type */
  emptyType?: EmptyStateType
  /** Empty state title override */
  emptyTitle?: string
  /** Empty state description override */
  emptyDescription?: string
  /** Empty state primary action */
  emptyPrimaryAction?: {
    label: string
    onClick: () => void
  }
  /** Empty state secondary action */
  emptySecondaryAction?: {
    label: string
    onClick: () => void
  }
  
  // Children
  /** Content to render when data is available */
  children: React.ReactNode
  
  // Styling
  /** Additional styling */
  sx?: object
}

// ============================================================================
// Helper Functions
// ============================================================================

const isEmpty = <T,>(data: T[] | T | null | undefined, minItems: number = 1): boolean => {
  if (!data) return true
  
  // Handle arrays
  if (Array.isArray(data)) {
    return data.length < minItems
  }
  
  // Handle objects
  if (typeof data === 'object') {
    return Object.keys(data).length === 0
  }
  
  // Handle primitives
  return !data
}

const isError = (error: string | null | undefined): boolean => {
  return Boolean(error && error.trim().length > 0)
}

// ============================================================================
// Main Component
// ============================================================================

const DataFetchWrapper = <T,>({
  loading,
  error,
  data,
  minItems = 1,
  
  // Loading props
  loadingVariant = 'spinner',
  loadingMessage,
  loadingRows = 3,
  
  // Error props
  errorTitle,
  onRetry,
  showErrorDetails = false,
  
  // Empty state props
  emptyType = 'no-data',
  emptyTitle,
  emptyDescription,
  emptyPrimaryAction,
  emptySecondaryAction,
  
  // Content
  children,
  
  // Styling
  sx = {}
}: DataFetchWrapperProps<T>) => {
  
  // Loading state
  if (loading) {
    return (
      <Box sx={sx}>
        <LoadingState
          variant={loadingVariant}
          message={loadingMessage}
          rows={loadingRows}
        />
      </Box>
    )
  }
  
  // Error state
  if (isError(error)) {
    return (
      <Box sx={sx}>
        <ErrorState
          message={error!}
          title={errorTitle}
          onRetry={onRetry}
          showRetry={Boolean(onRetry)}
          details={showErrorDetails ? error! : undefined}
          showDetails={showErrorDetails}
        />
      </Box>
    )
  }
  
  // Empty state
  if (isEmpty(data, minItems)) {
    return (
      <Box sx={sx}>
        <EmptyState
          type={emptyType}
          title={emptyTitle}
          description={emptyDescription}
          primaryAction={emptyPrimaryAction}
          secondaryAction={emptySecondaryAction}
        />
      </Box>
    )
  }
  
  // Success state - render children
  return <Box sx={sx}>{children}</Box>
}

export default DataFetchWrapper

// ============================================================================
// Specialized Wrapper Components
// ============================================================================

/**
 * 📊 Wrapper for table data with skeleton loading
 */
export const TableDataWrapper: React.FC<DataFetchWrapperProps & {
  /** Number of table columns for skeleton */
  columns?: number
}> = ({ columns = 5, ...props }) => (
  <DataFetchWrapper
    {...props}
    loadingVariant="table"
    loadingRows={columns}
  />
)

/**
 * 📋 Wrapper for card list data
 */
export const CardDataWrapper: React.FC<DataFetchWrapperProps> = (props) => (
  <DataFetchWrapper
    {...props}
    loadingVariant="card"
  />
)

/**
 * 🔍 Wrapper for search results
 */
export const SearchDataWrapper: React.FC<DataFetchWrapperProps & {
  /** Search query for empty state */
  searchQuery?: string
  /** Clear search handler */
  onClearSearch?: () => void
  /** Clear filters handler */
  onClearFilters?: () => void
}> = ({ 
  searchQuery, 
  onClearSearch, 
  onClearFilters,
  ...props 
}) => (
  <DataFetchWrapper
    {...props}
    emptyType="no-results"
    emptyDescription={
      searchQuery 
        ? `No results found for "${searchQuery}". Try a different search term or clear your filters.`
        : 'Your search didn\'t match any items. Try a different search term.'
    }
    emptyPrimaryAction={onClearSearch ? {
      label: 'Clear Search',
      onClick: onClearSearch
    } : undefined}
    emptySecondaryAction={onClearFilters ? {
      label: 'Clear Filters', 
      onClick: onClearFilters
    } : undefined}
  />
)

/**
 * 📦 Wrapper for product data
 */
export const ProductDataWrapper: React.FC<DataFetchWrapperProps> = (props) => (
  <DataFetchWrapper
    {...props}
    emptyType="no-products"
    loadingVariant="table"
  />
)

/**
 * 🧾 Wrapper for sales data
 */
export const SalesDataWrapper: React.FC<DataFetchWrapperProps> = (props) => (
  <DataFetchWrapper
    {...props}
    emptyType="no-sales"
    loadingVariant="table"
  />
)

/**
 * 👥 Wrapper for client data
 */
export const ClientDataWrapper: React.FC<DataFetchWrapperProps> = (props) => (
  <DataFetchWrapper
    {...props}
    emptyType="no-clients"
    loadingVariant="table"
  />
)

// ============================================================================
// Usage Example
// ============================================================================

/*
// Basic usage
<DataFetchWrapper
  loading={loading}
  error={error}
  data={products}
  emptyType="no-products"
  onRetry={refetch}
>
  <ProductTable products={products} />
</DataFetchWrapper>

// Table-specific wrapper
<TableDataWrapper
  loading={loading}
  error={error}
  data={sales}
  columns={6}
  emptyType="no-sales"
  onRetry={refetch}
>
  <SalesTable sales={sales} />
</TableDataWrapper>

// Search results wrapper
<SearchDataWrapper
  loading={loading}
  error={error}
  data={searchResults}
  searchQuery={query}
  onClearSearch={() => setQuery('')}
  onClearFilters={() => setFilters({})}
>
  <SearchResults results={searchResults} />
</SearchDataWrapper>
*/